import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MapPin, DollarSign, Building, BarChart3, Globe, Star, Stethoscope, Search, Bed, ShieldCheck, Heart, ArrowUpDown, ExternalLink, Award, ChevronRight, Landmark } from "lucide-react";
import { Link } from "react-router-dom";
import { stateAbbreviations } from "@/components/USAMapNavigator";
import { HospitalRankingsTable } from "@/components/map/HospitalRankingsTable";
import { getGradeInfo, getHospitalGradeInfo, scenarioMarkerColor } from "@/lib/grades";
import { titleCaseHospital } from "@/lib/hospitalName";
import { ScenarioRankingsPanel } from "@/components/map/ScenarioRankingsPanel";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useStaticHospitalData } from "@/hooks/useStaticHospitalData";
import { useHospitalScenarios } from "@/hooks/useHospitalScenarios";
import { isGarbagePrice } from "@/lib/priceSanity";
import { withScenario, citySlug } from "@/lib/mapNav";
import { useAhpHeaderOffset } from "@/components/AhpGlobalHeader";
import type { Hospital, HospitalStats, ScenarioInfo, ScenarioPricing } from "@/types/map";

/**
 * Panel-scoped error boundary. A data-shape surprise in the hospital detail
 * render degrades to a compact in-panel message (with a link to the full
 * profile) instead of bubbling up and blanking the whole map. Resets whenever
 * the selected hospital changes (via the `resetKey` prop → remount).
 */
class PanelErrorBoundary extends React.Component<
  { children: React.ReactNode; profileId?: string; accentColor: string },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err: unknown) { console.error('MapRightPanel render error:', err); }
  render() {
    if (this.state.hasError) {
      return <PanelErrorFallback profileId={this.props.profileId} accentColor={this.props.accentColor} />;
    }
    return this.props.children;
  }
}

/** Translated fallback body for PanelErrorBoundary (class components can't use hooks). */
function PanelErrorFallback({ profileId, accentColor }: { profileId?: string; accentColor: string }) {
  const { t } = useTranslation('map');
  return (
    <div className="p-6 text-center">
      <p className="text-sm text-muted-foreground">{t('panel.errorFallback')}</p>
      {profileId && (
        <Link to={`/hospital/${profileId}`} className={`text-xs ${accentColor} hover:underline mt-2 inline-block`}>
          {t('panel.openFullProfile')}
        </Link>
      )}
    </div>
  );
}

interface MapRightPanelProps {
  // Brand
  isAhp: boolean;
  panelBgGradient: string;
  panelCardBg: string;
  accentColor: string;

  // Debug
  debugToggles: { rightPanel: boolean };

  // Hospital data
  selectedHospital: Hospital | null;
  setSelectedHospital: (h: Hospital | null) => void;
  hospitals: Hospital[];
  hospitalStats: HospitalStats;
  hospitalEarnings: any;
  isLoadingEarnings: boolean;
  isDataReady: boolean;

  // Navigation
  navigate: (path: string, options?: any) => void;
  flyToState: (stateCode: string) => void;
  flyToCity: (city: string, state: string) => void;

  // Table state
  selectedTableState: string;
  setSelectedTableState: (s: string) => void;
  selectedTableCity: string;
  setSelectedTableCity: (s: string) => void;

  // Scenario
  activeScenarioId: string | null;
  setActiveScenarioId: (v: string | null) => void;
  scenarioInfo: ScenarioInfo | null;
  setSearchParams: (v: any) => void;

  // Scenario pricing
  scenarioHospitalPricing: ScenarioPricing;

  // Actions
  fetchHospitalProcedures: (hospitalId: string) => void;
  orbitHospital: (hospital: Hospital) => void;
}

export function MapRightPanel({
  isAhp,
  panelBgGradient,
  panelCardBg,
  accentColor,
  debugToggles,
  selectedHospital,
  setSelectedHospital,
  hospitals,
  hospitalStats,
  hospitalEarnings,
  isLoadingEarnings,
  isDataReady,
  navigate,
  flyToState,
  flyToCity,
  selectedTableState,
  setSelectedTableState,
  selectedTableCity,
  setSelectedTableCity,
  activeScenarioId,
  setActiveScenarioId,
  scenarioInfo,
  setSearchParams,
  scenarioHospitalPricing,
  fetchHospitalProcedures,
  orbitHospital,
}: MapRightPanelProps) {
  const ahpHeaderOffset = useAhpHeaderOffset();
  const { t } = useTranslation('map');

  // Rich per-hospital data from the static shard (same source the /hospital-rankings
  // + profile pages use): `ranking` = per-code-type breakdown, `meta` = revenue/IDs.
  const { ranking, meta: staticMeta } = useHospitalStatic(selectedHospital?.id);
  const { data: totalRanked } = useTotalRankedHospitals();

  return (
    /* Right Column - Hospital Analysis Tabs */
    <div className={`w-[600px] border-l border-border overflow-y-auto flex-shrink-0 z-[1] absolute right-0 top-0 hidden lg:block ${isAhp ? 'ahp-dark-panel' : ''}`} style={{ background: panelBgGradient, height: isAhp ? `calc(100vh - ${ahpHeaderOffset}px)` : "100vh", display: debugToggles.rightPanel ? '' : 'none' }}>
      <div className="p-8 space-y-8">
        <PanelErrorBoundary key={selectedHospital?.id || 'none'} profileId={selectedHospital?.id} accentColor={accentColor}>
        {selectedHospital ? (
          <>
            {/* \u2500\u2500 Hospital Header / Hero \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */}
            {(() => {
              const total = totalRanked || hospitals.length || 0;
              const rank = selectedHospital.overall_rank_gross || (ranking as any)?.overall_rank_gross || null;
              // Hospital-level headline metric: median-baseline markup (what
              // overall_rank_gross is ordered by); mean fallback until caches roll.
              const markup = (ranking as any)?.median_gross_markup_percentage
                ?? (selectedHospital as any).median_markup_percentage
                ?? (ranking as any)?.avg_gross_markup_percentage
                ?? selectedHospital.avg_markup_percentage;
              const gi = getHospitalGradeInfo(markup);
              const grade: string = gi.grade;
              const gradeHex = grade === 'N/A' ? '#94a3b8' : gradeColorHex(grade);
              const rankPct = rank && total ? (rank / total) * 100 : null;
              const rankTone: PanelTone = rank == null ? 'na'
                : rankPct! <= 10 ? 'bad' : rankPct! <= 50 ? 'neutral' : 'good';
              const rankBadge = rank == null ? t('panel.pending')
                : rank <= 10 ? t(isAhp ? 'panel.rankBadge.top10Expensive' : 'panel.rankBadge.hallOfShame')
                : rankPct! <= 10 ? t('panel.rankBadge.priciest10')
                : rankPct! <= 50 ? t('panel.rankBadge.aboveAverage')
                : t('panel.rankBadge.belowAverage');
              const totalProcs = (ranking as any)?.total_procedures ?? selectedHospital.total_procedures ?? 0;
              const outlierPct = totalProcs > 0 && (ranking as any)?.outlier_expensive_count != null
                ? ((ranking as any).outlier_expensive_count / totalProcs) * 100
                : (selectedHospital.outlier_percentage || 0);
              const outlierTone: PanelTone = !isDataReady ? 'na'
                : outlierPct >= 15 ? 'bad' : outlierPct >= 5 ? 'neutral' : 'good';
              // Tone tracks the letter grade so it stays calibrated to the
              // median-baseline scale (most hospitals sit above the median).
              const markupTone: PanelTone = markup == null ? 'na'
                : grade === 'F' ? 'bad' : (grade === 'A' || grade === 'B') ? 'good' : 'neutral';
              const stateName = (() => {
                const entry = Object.entries(stateAbbreviations).find(([_, code]) => code === selectedHospital.state);
                return entry ? entry[0] : selectedHospital.state;
              })();
              // Quality tone/values
              const cms = selectedHospital.cms_overall_rating ?? null;
              const gRating = selectedHospital.google_rating ?? null;
              const qualTone: PanelTone = cms == null ? 'na' : cms >= 4 ? 'good' : cms >= 3 ? 'neutral' : 'bad';

              return (
                <div className={`${panelCardBg} rounded-xl overflow-hidden`}>
                  {/* Breadcrumb */}
                  <div className="flex items-center gap-1.5 text-[11px] px-6 pt-5 pb-3 flex-wrap">
                    <button onClick={() => { setSelectedHospital(null); setSelectedTableState('USA'); setSelectedTableCity(''); navigate(withScenario('/map'), { replace: true }); }} className={`${accentColor} hover:underline`}>{t('rankings.allHospitals')}</button>
                    <ChevronRight className="w-3 h-3 text-muted-foreground/50" />
                    <button onClick={() => { setSelectedHospital(null); setSelectedTableState(selectedHospital.state); setSelectedTableCity(''); flyToState(selectedHospital.state); navigate(withScenario(`/map/${selectedHospital.state.toLowerCase()}`), { replace: true }); }} className={`${accentColor} hover:underline`}>{stateName}</button>
                    <ChevronRight className="w-3 h-3 text-muted-foreground/50" />
                    <button onClick={() => { setSelectedHospital(null); setSelectedTableState(selectedHospital.state); setSelectedTableCity(selectedHospital.city); flyToCity(selectedHospital.city, selectedHospital.state); navigate(withScenario(`/map/${selectedHospital.state.toLowerCase()}/${citySlug(selectedHospital.city)}`), { replace: true }); }} className={`${accentColor} hover:underline`}>{selectedHospital.city}, {selectedHospital.state}</button>
                  </div>

                  {/* Title row: grade medallion + name */}
                  <div className="px-6 flex items-start gap-4">
                    <div
                      className="shrink-0 flex flex-col items-center justify-center rounded-xl w-[68px] h-[68px] border"
                      style={{ background: `${gradeHex}1a`, borderColor: `${gradeHex}59`, boxShadow: `0 0 24px -8px ${gradeHex}80` }}
                      title={t('panel.gradeTitle')}
                    >
                      <span className="font-mono font-extrabold leading-none text-[34px]" style={{ color: gradeHex }}>{grade}</span>
                      <span className="text-[8px] uppercase tracking-[0.14em] font-bold mt-0.5" style={{ color: gradeHex }}>{t('panel.gradeLabel')}</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <h1 className="text-[22px] leading-tight font-bold text-foreground" style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif" } : {}}>{titleCaseHospital(selectedHospital.name)}</h1>
                      <p className="text-[13px] text-muted-foreground mt-0.5 flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5 shrink-0 opacity-70" />
                        <span className="truncate">{selectedHospital.address ? `${selectedHospital.address}, ` : ''}{selectedHospital.city}, {selectedHospital.state}</span>
                      </p>
                      <div className="flex items-center gap-3 mt-2">
                        <Link to={`/hospital/${selectedHospital.id}`} className={`inline-flex items-center gap-1 text-[11px] font-semibold ${accentColor} hover:underline`}>
                          <ExternalLink className="w-3 h-3" /> {t('panel.viewFullProfile')}
                        </Link>
                        {rank != null && (
                          <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                            <Award className="w-3 h-3" /> {t('panel.rankOf', { rank: rank.toLocaleString(), total: total.toLocaleString() })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Quick-facts chip row */}
                  <div className="px-6 mt-3 flex flex-wrap gap-1.5">
                    {selectedHospital.hospital_type && <QuickChip icon={<Building className="w-3 h-3" />}>{selectedHospital.hospital_type}</QuickChip>}
                    {selectedHospital.bed_count ? <QuickChip icon={<Bed className="w-3 h-3" />}>{t('rankings.beds', { n: selectedHospital.bed_count.toLocaleString() })}</QuickChip> : null}
                    {selectedHospital.ownership && <QuickChip icon={<Landmark className="w-3 h-3" />}>{selectedHospital.ownership}</QuickChip>}
                    {selectedHospital.emergency_services && <QuickChip icon={<Heart className="w-3 h-3" />} tone="bad">{t('rankings.er')}</QuickChip>}
                    {cms != null && <QuickChip icon={<Star className="w-3 h-3 fill-current" />} tone="gold">{t('panel.cmsChip', { rating: cms })}</QuickChip>}
                    {gRating != null && <QuickChip icon={<Star className="w-3 h-3 fill-current" />} tone="gold">{t('panel.googleChip', { rating: Number(gRating).toFixed(1) })}</QuickChip>}
                  </div>

                  {/* Signature stat grid */}
                  <div className="grid grid-cols-2 gap-3 p-6 pt-4">
                    <PanelStatCard
                      isAhp={isAhp} tone={rankTone}
                      label={t('panel.stats.nationalPriceRank')}
                      value={rank ? `#${rank.toLocaleString()}` : '\u2014'}
                      unit={rank ? t('panel.stats.ofTotal', { total: total.toLocaleString() }) : t('panel.stats.pending')}
                      pill={rankBadge}
                      sub={<CodeBreakdownChips ranking={ranking} />}
                      to="/hospital-rankings/"
                    />
                    <PanelStatCard
                      isAhp={isAhp} tone={markupTone}
                      label={t('panel.stats.markup')}
                      value={markup != null ? `${markup >= 0 ? '+' : ''}${markup.toFixed(0)}%` : '\u2014'}
                      unit={t('panel.stats.vsNationalMedian')}
                      pill={grade === 'N/A' ? undefined : t('panel.stats.gradePill', { grade })}
                      sub={t('panel.stats.acrossPricedServices', { n: totalProcs.toLocaleString() })}
                    />
                    <PanelStatCard
                      isAhp={isAhp} tone={outlierTone}
                      label={t('panel.stats.outlierRate')}
                      value={!isDataReady ? '\u2014' : `${outlierPct.toFixed(1)}%`}
                      unit={t('panel.stats.ofProcedures')}
                      pill={!isDataReady ? t('panel.pending') : outlierPct >= 40 ? t('panel.pill.extreme') : outlierPct >= 25 ? t('panel.pill.severe') : outlierPct >= 10 ? t('panel.pill.high') : t('panel.pill.low')}
                      sub={(ranking as any)?.outlier_expensive_count ? t('panel.stats.outlierCountSub', { n: Number((ranking as any).outlier_expensive_count).toLocaleString() }) : t('panel.stats.outlierSubFallback')}
                    />
                    <PanelStatCard
                      isAhp={isAhp} tone={qualTone}
                      label={t('panel.stats.qualityOfCare')}
                      value={cms != null ? `${cms}/5` : (gRating != null ? Number(gRating).toFixed(1) : '\u2014')}
                      unit={cms != null ? t('panel.stats.cmsStars') : (gRating != null ? t('panel.stats.googleRating') : t('panel.stats.notRated'))}
                      sub={gRating != null
                        ? (selectedHospital.google_review_count
                            ? t('panel.stats.googleSubWithReviews', { rating: Number(gRating).toFixed(1), n: selectedHospital.google_review_count.toLocaleString() })
                            : t('panel.stats.googleSub', { rating: Number(gRating).toFixed(1) }))
                        : t('panel.stats.cmsCareCompare')}
                      to={`/hospital/${selectedHospital.id}/quality`}
                    />
                  </div>

                  {/* CMS comparative quality measures \u2014 mortality, safety,
                      readmission, patient experience vs the national rate.
                      Same data the mobile sheet shows; the desktop panel
                      previously never surfaced it. */}
                  {(() => {
                    const dir = (v: string | null | undefined): { sym: string; color: string; word: string } | null => {
                      if (!v) return null;
                      const s = v.toLowerCase();
                      // CMS normalizes direction: "Above" = better performance
                      // on every comparative group.
                      if (s.startsWith('above')) return { sym: '\u25b2', color: '#16a34a', word: t('panel.quality.better') };
                      if (s.startsWith('below')) return { sym: '\u25bc', color: '#dc2626', word: t('panel.quality.worse') };
                      if (s.startsWith('same') || s.startsWith('no different')) return { sym: '=', color: '#9ca3af', word: t('panel.quality.same') };
                      return null;
                    };
                    const measures = ([
                      [t('panel.quality.deathRate'), dir(selectedHospital.cms_mortality_rating)],
                      [t('panel.quality.safetyOfCare'), dir(selectedHospital.cms_safety_rating)],
                      [t('panel.quality.readmission'), dir(selectedHospital.cms_readmission_rating)],
                      [t('panel.quality.patientExperience'), dir(selectedHospital.cms_patient_experience_rating)],
                    ] as const).filter((m): m is [string, { sym: string; color: string; word: string }] => m[1] != null);
                    if (!measures.length) return null;
                    return (
                      <div className="px-6 pb-5 -mt-1">
                        <div className={`rounded-lg border px-4 py-3 ${isAhp ? 'border-[#C5A55A]/20' : 'border-border/40'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] uppercase tracking-[0.12em] font-bold text-muted-foreground">{t('panel.quality.title')}</span>
                            <Link to={`/hospital/${selectedHospital.id}/quality`} className={`text-[11px] ${accentColor} hover:underline`}>{t('panel.quality.allMeasures')}</Link>
                          </div>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                            {measures.map(([label, d]) => (
                              <div key={label} className="flex items-center justify-between text-xs" title={d.word}>
                                <span className="text-muted-foreground">{label}</span>
                                <span className="font-bold" style={{ color: d.color }}>{d.sym}</span>
                              </div>
                            ))}
                          </div>
                          <div className="mt-1.5 text-[10px] text-muted-foreground/70">{t('panel.quality.legend')}</div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              );
            })()}

            {/* Hospital Tabs Container */}
            <div className={`${panelCardBg} rounded-lg`}>
              <Tabs defaultValue={activeScenarioId ? "scenarios" : "overview"} key={activeScenarioId || 'default'} className="w-full">
                <TabsList className="grid w-full grid-cols-5 h-10 bg-muted/50 rounded-t-lg rounded-b-none border-b">
                  <TabsTrigger value="overview" className="flex items-center gap-1 text-xs">
                    <Building className="h-3 w-3" />
                    {t('panels.right.hospitalDetails')}
                  </TabsTrigger>
                  <TabsTrigger value="scenarios" className="flex items-center gap-1 text-xs">
                    <Stethoscope className="h-3 w-3" />
                    {t('scenario.activeBadge')}
                  </TabsTrigger>
                  <TabsTrigger value="pricing" className="flex items-center gap-1 text-xs">
                    <DollarSign className="h-3 w-3" />
                    {t('panels.right.procedures')}
                  </TabsTrigger>
                  <TabsTrigger value="earnings" className="flex items-center gap-1 text-xs">
                    <BarChart3 className="h-3 w-3" />
                    {t('panels.right.earnings')}
                  </TabsTrigger>
                  <TabsTrigger value="global" className="flex items-center gap-1 text-xs">
                    <Globe className="h-3 w-3" />
                    {t('panel.tabs.global')}
                  </TabsTrigger>
                </TabsList>

              {/* Overview Tab — price breakdown, scenario preview, transparency */}
              <TabsContent value="overview" className="space-y-5 p-5 m-0">
                {/* Active scenario preview — surfaced first when a scenario is on */}
                {activeScenarioId && scenarioInfo && scenarioHospitalPricing.has(selectedHospital.id) && (() => {
                  const sp = scenarioHospitalPricing.get(selectedHospital.id)!;
                  const overNat = sp.blendedMarkup;
                  const tone = overNat > 50 ? '#f87171' : overNat > 0 ? '#e5b968' : '#34d399';
                  return (
                    <div className={`rounded-xl p-4 border ${isAhp ? 'bg-[#C5A55A]/10 border-[#C5A55A]/30' : 'bg-primary/5 border-primary/20'}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <Stethoscope className={`w-4 h-4 ${isAhp ? 'text-[#C5A55A]' : 'text-primary'}`} />
                        <span className="text-sm font-semibold text-foreground truncate">{scenarioInfo.name}</span>
                        <span className="ml-auto text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ background: `${tone}22`, color: tone }}>
                          {t('panel.scenario.vsNational', { pct: `${overNat > 0 ? '+' : ''}${overNat.toFixed(0)}%` })}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className="text-[11px] text-muted-foreground">{t('panel.scenario.thisHospital')}</div>
                          <div className="text-xl font-bold font-mono" style={{ color: tone }}>${Math.round(sp.blendedPrice).toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-[11px] text-muted-foreground">{t('panel.scenario.nationalAvg')}</div>
                          <div className="text-xl font-bold font-mono text-muted-foreground">${scenarioInfo.nationalAvgPrice.toLocaleString()}</div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Price breakdown by billing-code type */}
                <div>
                  <h4 className="text-[11px] uppercase tracking-[0.1em] font-bold text-muted-foreground mb-3 flex items-center gap-2">
                    <BarChart3 className="h-3.5 w-3.5" /> {t('panel.overview.priceBreakdown')}
                  </h4>
                  <CodeBreakdownBars ranking={ranking} />
                </div>

                {/* Transparency of the published file */}
                <div>
                  <h4 className="text-[11px] uppercase tracking-[0.1em] font-bold text-muted-foreground mb-3 flex items-center gap-2">
                    <ShieldCheck className="h-3.5 w-3.5" /> {t('panel.overview.fileTransparency')}
                  </h4>
                  <div className="grid grid-cols-3 gap-2">
                    <MiniStat label={t('panel.overview.obfuscation')} value={selectedHospital.data_obfuscation_score != null ? selectedHospital.data_obfuscation_score.toFixed(1) : '—'} isAhp={isAhp} />
                    <MiniStat label={t('panel.overview.fileSize')} value={selectedHospital.avg_file_size_mb != null ? `${selectedHospital.avg_file_size_mb.toFixed(1)}MB` : '—'} isAhp={isAhp} />
                    <MiniStat label={t('panel.overview.complexity')} value={selectedHospital.file_complexity_score != null ? selectedHospital.file_complexity_score.toFixed(1) : '—'} isAhp={isAhp} />
                  </div>
                </div>

                {/* Footer note */}
                <p className="text-[11px] text-muted-foreground leading-relaxed pt-1 border-t border-border/20">
                  {t('panel.overview.footerNote', { n: hospitalStats.followed.toLocaleString() })}{' '}
                  <Link to={`/hospital/${selectedHospital.id}`} className={`${accentColor} hover:underline font-medium`}>{t('panel.overview.seeFullProfile')}</Link>
                </p>
              </TabsContent>

              {/* Scenarios Tab — NEW */}
              <TabsContent value="scenarios" className="p-4 m-0">
                <HospitalScenariosTab
                  hospitalId={selectedHospital.id}
                  isAhp={isAhp}
                  activeScenarioId={activeScenarioId}
                  onScenarioSelect={(scenarioId) => {
                    setActiveScenarioId(scenarioId);
                    setSearchParams({ scenario: scenarioId });
                  }}
                />
              </TabsContent>

              {/* Pricing Tab */}
              <TabsContent value="pricing" className="space-y-5 p-5 m-0">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-foreground">{t('panel.pricing.title')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="p-4 bg-background/30 rounded-lg border border-border">
                      <h4 className="font-medium text-foreground mb-2">{t('panel.pricing.avgMarkup')}</h4>
                      <div className="text-2xl font-bold text-primary">{selectedHospital.avg_markup_percentage?.toFixed(1)}%</div>
                      <p className="text-sm text-muted-foreground mt-1">{t('panel.pricing.vsIngested', { count: hospitalStats.followed })}</p>
                    </div>
                    <div className="p-4 bg-background/30 rounded-lg border border-border">
                      <h4 className="font-medium text-foreground mb-2">{t('tooltip.totalProcedures')}</h4>
                      <div className="text-2xl font-bold text-primary">{selectedHospital.total_procedures?.toLocaleString()}</div>
                      <p className="text-sm text-muted-foreground mt-1">{t('panel.pricing.trackedServices')}</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-medium text-foreground">{t('panel.pricing.transparency')}</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between p-3 bg-background/20 rounded border">
                        <span className="text-muted-foreground">{t('panel.pricing.obfuscationScore')}</span>
                        <span className="text-foreground">{selectedHospital.data_obfuscation_score?.toFixed(1) || t('panel.pending')}</span>
                      </div>
                      <div className="flex justify-between p-3 bg-background/20 rounded border">
                        <span className="text-muted-foreground">{t('panel.pricing.avgFileSize')}</span>
                        <span className="text-foreground">{selectedHospital.avg_file_size_mb?.toFixed(1) || t('panel.pricing.na')} MB</span>
                      </div>
                      <div className="flex justify-between p-3 bg-background/20 rounded border">
                        <span className="text-muted-foreground">{t('panel.pricing.complexityScore')}</span>
                        <span className="text-foreground">{selectedHospital.file_complexity_score?.toFixed(1) || t('panel.pricing.na')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Earnings / Financials Tab — real reported revenue from the static
                  shard's meta (IRS 990 / CMS cost report); DB estimate as fallback. */}
              <TabsContent value="earnings" className="space-y-5 p-5 m-0">
                {(() => {
                  const rev = staticMeta?.annual_revenue_usd != null ? Number(staticMeta.annual_revenue_usd) : null;
                  const fmtRev = (v: number) =>
                    v >= 1e9 ? `$${(v / 1e9).toFixed(v >= 1e10 ? 0 : 1)}B`
                    : v >= 1e6 ? `$${(v / 1e6).toFixed(0)}M`
                    : v >= 1e3 ? `$${(v / 1e3).toFixed(0)}K`
                    : `$${Math.round(v)}`;

                  if (rev && rev > 0) {
                    return (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <PanelStatCard
                            isAhp={isAhp} tone="info"
                            label={t('panel.earnings.annualRevenue')}
                            value={fmtRev(rev)}
                            unit={staticMeta.revenue_year ? t('panel.earnings.fy', { year: staticMeta.revenue_year }) : t('panel.earnings.reported')}
                            sub={staticMeta.revenue_source || t('panel.earnings.publicFilings')}
                          />
                          <PanelStatCard
                            isAhp={isAhp} tone="neutral"
                            label={t('panel.earnings.revenuePerBed')}
                            value={selectedHospital.bed_count ? fmtRev(rev / selectedHospital.bed_count) : '—'}
                            unit={selectedHospital.bed_count ? t('rankings.beds', { n: selectedHospital.bed_count.toLocaleString() }) : t('panel.earnings.noBedData')}
                            sub={t('panel.earnings.revenuePerBedSub')}
                          />
                        </div>
                        {(staticMeta.hospital_system || staticMeta.ein || staticMeta.ccn) && (
                          <div className="grid grid-cols-3 gap-2">
                            {staticMeta.hospital_system && <MiniStat label={t('panel.earnings.system')} value={<span className="text-[11px] leading-tight block truncate" title={staticMeta.hospital_system}>{staticMeta.hospital_system}</span>} isAhp={isAhp} />}
                            {staticMeta.ccn && <MiniStat label={t('panel.earnings.ccn')} value={<span className="text-sm font-mono">{staticMeta.ccn}</span>} isAhp={isAhp} />}
                            {staticMeta.ein && <MiniStat label={t('panel.earnings.ein')} value={<span className="text-sm font-mono">{staticMeta.ein}</span>} isAhp={isAhp} />}
                          </div>
                        )}
                        <p className="text-[11px] text-muted-foreground leading-relaxed pt-1 border-t border-border/20">
                          {t('panel.earnings.sourceNote')}{' '}
                          <Link to={`/hospital/${selectedHospital.id}/financial`} className={`${accentColor} hover:underline font-medium`}>{t('panel.earnings.seeFullFinancials')}</Link>
                        </p>
                      </>
                    );
                  }

                  // Fallback: estimated earnings from the DB (only if the shard has no revenue).
                  if (isLoadingEarnings) {
                    return (
                      <div className="flex items-center justify-center py-10">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                      </div>
                    );
                  }
                  if (hospitalEarnings) {
                    return (
                      <div className="grid grid-cols-2 gap-3">
                        <PanelStatCard isAhp={isAhp} tone="good" label={t('panel.earnings.estAnnualRevenue')} value={`$${Math.round(Number(hospitalEarnings.estimated_annual_revenue || 0)).toLocaleString()}`} unit={t('panel.earnings.estimated')} sub={t('panel.earnings.fromTracked')} />
                        <PanelStatCard isAhp={isAhp} tone="bad" label={t('panel.earnings.markupRevenue')} value={`$${Math.round(Number(hospitalEarnings.markup_revenue || 0)).toLocaleString()}`} unit={t('panel.earnings.overNationalAvg')} sub={t('panel.earnings.vsIndexed', { n: hospitalStats.followed.toLocaleString() })} />
                      </div>
                    );
                  }
                  return (
                    <div className="text-center py-10">
                      <BarChart3 className="w-8 h-8 mx-auto mb-3 text-muted-foreground/40" />
                      <p className="text-sm text-muted-foreground">{t('panel.earnings.noFinancials')}</p>
                      <Link to={`/hospital/${selectedHospital.id}/financial`} className={`text-xs ${accentColor} hover:underline mt-1 inline-block`}>{t('panel.earnings.checkFullProfile')}</Link>
                    </div>
                  );
                })()}
              </TabsContent>

              {/* Global Tab */}
              <TabsContent value="global" className="space-y-5 p-5 m-0">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-foreground">{t('panel.global.title')}</h3>
                  <div className="space-y-4">
                    <div className="p-4 bg-background/30 rounded-lg border border-border">
                      <h4 className="font-medium text-foreground mb-2">{t('panel.global.priceComparison')}</h4>
                      <p className="text-sm text-muted-foreground mb-3">{t('panel.global.priceComparisonSub', { count: hospitalStats.followed })}</p>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{t('panel.global.usAverage')}</span>
                          <span className="text-foreground">{t('panel.global.baseline')}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{t('panel.global.thisHospital')}</span>
                          <span className="text-primary font-medium">{selectedHospital.avg_markup_percentage?.toFixed(1)}%</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{t('panel.global.intlAverage')}</span>
                          <span className="text-green-400">{t('panel.global.intlLower')}</span>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 bg-background/30 rounded-lg border border-border">
                      <h4 className="font-medium text-foreground mb-2">{t('panel.global.marketPosition')}</h4>
                      <p className="text-sm text-muted-foreground mb-3">{t('panel.global.marketPositionSub')}</p>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{t('panel.global.transparencyRanking')}</span>
                          <span className="text-foreground">{selectedHospital.transparency_grade || t('panel.global.notRated')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{t('panel.global.priceOutliers')}</span>
                          <span className="text-red-400">{(selectedHospital.outlier_percentage || 0).toFixed(1)}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
            </div>
          </>
        ) : activeScenarioId && scenarioInfo ? (
          /* Scenario Rankings Panel when scenario mode is active */
          <ScenarioRankingsPanel
            scenarioId={activeScenarioId}
            scenarioName={scenarioInfo.name}
            nationalAvgPrice={scenarioInfo.nationalAvgPrice}
            onHospitalClick={(hospitalId) => {
              const hospital = hospitals.find(h => h.id === hospitalId);
              if (hospital) {
                setSelectedHospital(hospital);
                fetchHospitalProcedures(hospital.id);
                orbitHospital(hospital);
              }
            }}
            onClear={() => {
              setActiveScenarioId(null);
              setSearchParams({});
            }}
          />
        ) : (
          /* Hospital Rankings Table when no hospital selected */
          <HospitalRankingsTable
            selectedState={selectedTableState}
            selectedCity={selectedTableCity}
            onStateChange={(state) => {
              setSelectedTableState(state);
              if (state === 'USA') {
                navigate(withScenario('/map'), { replace: true });
              } else {
                navigate(withScenario(`/map/${state.toLowerCase()}`), { replace: true });
              }
            }}
            onCityChange={(city) => {
              setSelectedTableCity(city);
              if (city && selectedTableState !== 'USA') {
                navigate(withScenario(`/map/${selectedTableState.toLowerCase()}/${citySlug(city)}`), { replace: true });
                flyToCity(city, selectedTableState);
              } else if (!city && selectedTableState !== 'USA') {
                navigate(withScenario(`/map/${selectedTableState.toLowerCase()}`), { replace: true });
              }
            }}
            onStateSelect={flyToState}
            onHospitalClick={(hospitalId) => {
              const hospital = hospitals.find(h => h.id === hospitalId);
              if (hospital) {
                setSelectedHospital(hospital);
                fetchHospitalProcedures(hospital.id);
                orbitHospital(hospital);
                navigate(withScenario(`/map/${hospital.state.toLowerCase()}/${hospital.id}`), { replace: true });
              }
            }}
          />
        )}
        </PanelErrorBoundary>
      </div>
    </div>
  );
}

// ─── Hospital Scenarios Tab ─────────────────────────────────────────
interface HospitalScenariosTabProps {
  hospitalId: string;
  isAhp: boolean;
  activeScenarioId: string | null;
  onScenarioSelect: (scenarioId: string) => void;
}

function HospitalScenariosTab({ hospitalId, isAhp, activeScenarioId, onScenarioSelect }: HospitalScenariosTabProps) {
  const { t } = useTranslation('map');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortAsc, setSortAsc] = useState(false); // worst markup first

  // JSON-first: reads `hospital_scenarios/{id}.json` (R2/CDN) with a Supabase
  // fallback, blended prices + names already resolved. Same hook the bottom panel
  // and profile page use — no direct DB query from the panel anymore.
  const { scenarios: scenarioRows, isLoading } = useHospitalScenarios(hospitalId);

  const filtered = scenarioRows.filter((r) => {
    if (!searchTerm) return true;
    const name = r.scenario.name || '';
    const cat = r.scenario.category || '';
    return name.toLowerCase().includes(searchTerm.toLowerCase()) || cat.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const sorted = [...filtered].sort((a, b) => {
    const diff = a.blendedMarkup - b.blendedMarkup;
    return sortAsc ? diff : -diff;
  });

  const getMarkupColor = (markup: number) => {
    return getGradeInfo(markup).twColor;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (scenarioRows.length === 0) {
    return (
      <div className="text-center py-12">
        <Stethoscope className="w-8 h-8 mx-auto mb-3 text-muted-foreground/40" />
        <p className="text-sm text-muted-foreground">{t('panel.scenarios.noData')}</p>
        <p className="text-xs text-muted-foreground mt-1">{t('panel.scenarios.selectHint')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            placeholder={t('panel.scenarios.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 h-8 text-xs"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setSortAsc(!sortAsc)}
          className="h-8 text-xs gap-1 shrink-0"
        >
          <ArrowUpDown className="w-3 h-3" />
          {sortAsc ? t('panel.scenarios.bestFirst') : t('panel.scenarios.worstFirst')}
        </Button>
      </div>

      <div className="text-xs text-muted-foreground">{t('panel.scenarios.countWithPricing', { count: sorted.length })}</div>

      <div className="space-y-1 max-h-[calc(100vh-520px)] overflow-y-auto">
        {sorted.map((row) => {
          const s = row.scenario;
          const nationalCost = row.nationalCost || 0;
          const isActive = row.scenario_id === activeScenarioId;
          const blendedPrice = row.blendedPrice;
          const blendedMarkup = row.blendedMarkup;

          return (
            <button
              key={row.scenario_id}
              onClick={() => onScenarioSelect(row.scenario_id)}
              className={`w-full text-left p-3 rounded-lg border transition-all ${
                isActive
                  ? (isAhp ? 'bg-[#C5A55A]/15 border-[#C5A55A]/40' : 'bg-primary/10 border-primary/30')
                  : (isAhp ? 'bg-[#001c48]/30 hover:bg-[#001c48]/50 border-[#C5A55A]/10' : 'bg-muted/10 hover:bg-muted/20 border-border/30')
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">{s.name}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    {s.category} &middot; {t('panel.scenarios.codesMatched', { matched: row.matched_codes, total: row.total_codes })}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-bold font-mono text-foreground">
                    {row.matched_codes < row.total_codes ? '~' : ''}${Math.round(blendedPrice).toLocaleString()}
                  </div>
                  <div className={`text-xs font-semibold ${getMarkupColor(blendedMarkup)}`}>
                    {t('panel.scenarios.vsAvg', { pct: `${blendedMarkup > 0 ? '+' : ''}${blendedMarkup.toFixed(0)}%` })}
                  </div>
                </div>
              </div>
              {nationalCost > 0 && (
                <div className="mt-1.5 flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-muted/30 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${
                        blendedMarkup > 100 ? 'bg-red-500' :
                        blendedMarkup > 50 ? 'bg-orange-500' :
                        blendedMarkup > 0 ? 'bg-yellow-500' :
                        'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(100, Math.max(5, (blendedPrice / (nationalCost * 3)) * 100))}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                    {t('panel.scenarios.natlAvg', { price: `$${nationalCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}` })}
                  </span>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Redesigned panel building blocks ───────────────────────────────
type PanelTone = 'good' | 'neutral' | 'bad' | 'na' | 'info';

const PANEL_TONE: Record<PanelTone, { fill: string; soft: string; pillBg: string; pillBd: string; pillTx: string }> = {
  good:    { fill: '#34d399', soft: 'rgba(16,185,129,0.10)',  pillBg: 'rgba(16,185,129,0.18)', pillBd: 'rgba(16,185,129,0.5)',  pillTx: '#86efac' },
  neutral: { fill: '#e5b968', soft: 'rgba(197,165,90,0.10)',  pillBg: 'rgba(197,165,90,0.20)', pillBd: 'rgba(197,165,90,0.55)', pillTx: '#f5ebd7' },
  bad:     { fill: '#f87171', soft: 'rgba(178,34,52,0.14)',   pillBg: 'rgba(220,38,38,0.22)',  pillBd: 'rgba(248,113,113,0.5)', pillTx: '#fca5a5' },
  info:    { fill: '#7bc0e0', soft: 'rgba(91,156,192,0.10)',  pillBg: 'rgba(91,156,192,0.20)', pillBd: 'rgba(123,192,224,0.5)', pillTx: '#bce0f2' },
  na:      { fill: '#94a3b8', soft: 'rgba(255,255,255,0.03)', pillBg: 'rgba(255,255,255,0.08)',pillBd: 'rgba(255,255,255,0.22)',pillTx: '#cbd5e1' },
};

/** Hex for a letter grade (matches lib/grades gradeColor but usable inline). */
function gradeColorHex(grade: string): string {
  switch (grade) {
    case 'A': return '#34d399';
    case 'B': return '#a3e635';
    case 'C': return '#e5b968';
    case 'D': return '#fb923c';
    case 'F': return '#f87171';
    default:  return '#94a3b8';
  }
}

/** Signature stat card — flag-stripe accent, mono number, tone coloring.
    Mirrors the /hospital/:id profile page's StatCard on the dark map panel. */
function PanelStatCard({
  isAhp, tone, label, value, unit, sub, pill, to,
}: {
  isAhp: boolean;
  tone: PanelTone;
  label: React.ReactNode;
  value: React.ReactNode;
  unit?: string;
  sub?: React.ReactNode;
  pill?: string;
  to?: string;
}) {
  const c = PANEL_TONE[tone];
  const border = isAhp ? 'rgba(197,165,90,0.18)' : 'rgba(255,255,255,0.10)';
  const cardBg = isAhp
    ? 'linear-gradient(180deg, rgba(2,22,60,0.55) 0%, rgba(0,14,44,0.65) 100%)'
    : 'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)';
  const inner = (
    <div
      className="relative h-full rounded-lg p-3.5 pl-4 transition-shadow hover:shadow-[0_0_0_1px_rgba(197,165,90,0.4)]"
      style={{ background: `${cardBg}, ${c.soft}`, backgroundBlendMode: 'normal, screen', border: `1px solid ${border}` }}
    >
      <div className="absolute top-2 bottom-2 left-0 w-[3px] rounded-full" style={{ background: c.fill, boxShadow: `0 0 8px ${c.fill}66` }} aria-hidden />
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="text-[9.5px] uppercase tracking-[0.09em] font-bold text-muted-foreground leading-tight">{label}</div>
        {pill && (
          <span className="text-[8.5px] uppercase tracking-[0.06em] font-bold px-1.5 py-[1px] rounded-sm border whitespace-nowrap shrink-0"
            style={{ background: c.pillBg, borderColor: c.pillBd, color: c.pillTx }}>
            {pill}
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-1.5 flex-wrap mt-0.5">
        <span className="font-mono text-[26px] font-extrabold tabular-nums leading-none" style={{ color: c.fill, letterSpacing: '-0.02em' }}>{value}</span>
        {unit && <span className="text-[10px] font-medium text-foreground leading-tight">{unit}</span>}
      </div>
      {sub && <div className="text-[10px] mt-1.5 text-muted-foreground leading-snug">{sub}</div>}
    </div>
  );
  if (to) return <Link to={to} className="block h-full">{inner}</Link>;
  return inner;
}

/** Small pill chip for the quick-facts row (beds, type, ownership, ER, ratings). */
function QuickChip({ icon, children, tone }: { icon?: React.ReactNode; children: React.ReactNode; tone?: 'default' | 'bad' | 'gold' }) {
  const styles = tone === 'bad'
    ? { color: '#fca5a5', border: 'rgba(248,113,113,0.4)', bg: 'rgba(220,38,38,0.12)' }
    : tone === 'gold'
    ? { color: '#f5d98a', border: 'rgba(197,165,90,0.4)', bg: 'rgba(197,165,90,0.12)' }
    : { color: 'inherit', border: 'rgba(255,255,255,0.14)', bg: 'rgba(255,255,255,0.05)' };
  return (
    <span className="inline-flex items-center gap-1 text-[10.5px] font-medium px-2 py-1 rounded-md text-foreground"
      style={{ border: `1px solid ${styles.border}`, background: styles.bg, color: styles.color }}>
      {icon}{children}
    </span>
  );
}

/** Code-type buckets shared by the header chips and the overview bars. */
const CODE_BUCKETS: Array<{ label: string; countKeys: string[]; markupKeys: string[] }> = [
  { label: 'CPT',    countKeys: ['cpt_procedure_count'],    markupKeys: ['cpt_avg_markup_percentage'] },
  { label: 'HCPCS',  countKeys: ['hcpcs_procedure_count'],  markupKeys: ['hcpcs_avg_markup_percentage'] },
  { label: 'DRG',    countKeys: ['drg_procedure_count'],    markupKeys: ['drg_avg_markup_percentage'] },
  { label: 'REV',    countKeys: ['rev_procedure_count'],    markupKeys: ['rev_avg_markup_percentage'] },
  { label: 'DRUG',   countKeys: ['drug_procedure_count', 'ndc_procedure_count'], markupKeys: ['drug_avg_markup_percentage', 'ndc_avg_markup_percentage'] },
  { label: 'SUPPLY', countKeys: ['supply_procedure_count'], markupKeys: ['supply_avg_markup_percentage'] },
];

function firstNum(obj: any, keys: string[]): number | null {
  for (const k of keys) { const v = obj?.[k]; if (v != null && isFinite(Number(v))) return Number(v); }
  return null;
}
function buildBuckets(ranking: any) {
  if (!ranking) return [] as Array<{ label: string; count: number; markup: number | null }>;
  return CODE_BUCKETS
    .map(b => ({ label: b.label, count: firstNum(ranking, b.countKeys) ?? 0, markup: firstNum(ranking, b.markupKeys) }))
    .filter(b => b.count > 0);
}
function fmtCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return n.toLocaleString();
}
function markupHex(m: number | null): string {
  if (m == null) return '#94a3b8';
  return m > 50 ? '#f87171' : m > 0 ? '#e5b968' : '#34d399';
}

/** Inline code-type markup chips shown under the "National price rank" card. */
function CodeBreakdownChips({ ranking }: { ranking: any }) {
  const { t } = useTranslation('map');
  const buckets = buildBuckets(ranking);
  if (buckets.length === 0) return <span>{t('panel.overview.weightedMarkupNote')}</span>;
  return (
    <span className="flex flex-wrap gap-x-2 gap-y-0.5 font-mono text-[9.5px] mt-0.5">
      {buckets.map(b => {
        const pct = b.markup == null ? null : `${b.markup >= 0 ? '+' : ''}${b.markup.toFixed(0)}%`;
        return (
          <span key={b.label} className="inline-flex items-baseline gap-0.5 whitespace-nowrap">
            <span className="font-semibold text-foreground">{b.label}</span>
            <span className="opacity-50">{fmtCount(b.count)}</span>
            {pct && <span className="font-bold" style={{ color: markupHex(b.markup) }}>{pct}</span>}
          </span>
        );
      })}
    </span>
  );
}

/** Horizontal bars: markup per code type, centered on the national average. */
function CodeBreakdownBars({ ranking }: { ranking: any }) {
  const { t } = useTranslation('map');
  const buckets = buildBuckets(ranking);
  if (buckets.length === 0) {
    return <div className="text-[11px] text-muted-foreground py-2">{t('panel.overview.noCodeBreakdown')}</div>;
  }
  // Scale bars against the largest absolute markup in the set (min 60% headroom).
  const maxAbs = Math.max(60, ...buckets.map(b => Math.abs(b.markup ?? 0)));
  return (
    <div className="space-y-2">
      {buckets.map(b => {
        const m = b.markup ?? 0;
        const widthPct = Math.min(50, (Math.abs(m) / maxAbs) * 50);
        const hex = markupHex(b.markup);
        return (
          <div key={b.label} className="flex items-center gap-2">
            <span className="w-12 shrink-0 text-[10px] font-bold text-foreground">{b.label}</span>
            <span className="w-9 shrink-0 text-[9px] text-muted-foreground text-right tabular-nums">{fmtCount(b.count)}</span>
            {/* Diverging bar: center line = national avg */}
            <div className="relative flex-1 h-2.5 rounded-full bg-white/5 overflow-hidden">
              <div className="absolute top-0 bottom-0 left-1/2 w-px bg-white/25" aria-hidden />
              <div
                className="absolute top-0 bottom-0 rounded-full"
                style={m >= 0
                  ? { left: '50%', width: `${widthPct}%`, background: hex }
                  : { right: '50%', width: `${widthPct}%`, background: hex }}
              />
            </div>
            <span className="w-11 shrink-0 text-[10px] font-bold text-right tabular-nums" style={{ color: hex }}>
              {b.markup == null ? '—' : `${m >= 0 ? '+' : ''}${m.toFixed(0)}%`}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Compact metric tile used in the File transparency row. */
function MiniStat({ label, value, isAhp }: { label: string; value: React.ReactNode; isAhp: boolean }) {
  return (
    <div className={`rounded-lg border p-2.5 text-center ${isAhp ? 'bg-[#001c48]/40 border-[#C5A55A]/15' : 'bg-white/5 border-white/10'}`}>
      <div className="text-base font-bold font-mono text-foreground tabular-nums">{value}</div>
      <div className="text-[9px] uppercase tracking-[0.08em] text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

// ─── Data hooks ─────────────────────────────────────────────────────
// JSON-first (R2 CDN / offline cache), Supabase only as a fallback — mirrors
// how the /hospital/:id profile + /hospital-rankings pages source their data.

/** Static per-hospital shard (`hospitals/{id}.json`) → `{ ranking, meta }`.
    `ranking` prefers the JSON and hits the DB only if the shard is missing;
    `meta` (revenue, ownership, IDs) is JSON-only — the map already supplies the
    base display fields on `selectedHospital`, so there's no DB fallback needed. */
function useHospitalStatic(hospitalId?: string) {
  const staticQ = useStaticHospitalData(hospitalId);
  const staticRanking = (staticQ.data?.ranking as any) ?? null;
  const meta = (staticQ.data?.meta as any) ?? null;

  const dbQ = useQuery({
    queryKey: ['map-panel-ranking-db', hospitalId],
    // Only reach for the DB once the static shard has settled with no ranking.
    enabled: !!hospitalId && staticQ.isFetched && !staticRanking,
    staleTime: 30 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('hospital_ranking_summary')
        .select(`
          avg_gross_markup_percentage, median_gross_markup_percentage, total_procedures, outlier_expensive_count, overall_rank_gross,
          cpt_avg_markup_percentage, hcpcs_avg_markup_percentage, drg_avg_markup_percentage,
          rev_avg_markup_percentage, ndc_avg_markup_percentage, drug_avg_markup_percentage, supply_avg_markup_percentage,
          cpt_procedure_count, hcpcs_procedure_count, drg_procedure_count, rev_procedure_count,
          ndc_procedure_count, drug_procedure_count, supply_procedure_count
        `)
        .eq('hospital_id', hospitalId)
        .maybeSingle();
      if (error && error.code !== 'PGRST116') { console.error('Panel ranking DB fallback error:', error); return null; }
      return data;
    },
  });

  return { ranking: staticRanking ?? dbQ.data ?? null, meta };
}

/** Total number of ranked hospitals (for the "#X of N" denominator).
    A single count-only, session-cached query. `rankings.json` can't be used here:
    it's the quality-filtered *display* list (~4.9k), not the full ranking universe
    (~7.5k) that `overall_rank_gross` is numbered against, so its length would
    understate the denominator and disagree with the profile page. */
function useTotalRankedHospitals() {
  return useQuery({
    queryKey: ['total-ranked-hospitals'],
    staleTime: 30 * 60 * 1000,
    queryFn: async () => {
      const { count, error } = await supabase
        .from('hospital_ranking_summary')
        .select('*', { count: 'exact', head: true });
      if (error) { console.error('Total ranked error:', error); return 0; }
      return count || 0;
    },
  });
}

export default MapRightPanel;
